import { YES_NO, YesNoDataSource } from 'components/v2/compiler/constants';
import WizardSelectComponent from 'components/v2/epd-wizard-inputs/WizardSelect';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { LabelGroup } from 'components/v2/form/styled';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { useTranslation } from 'react-i18next';
import { EPDModel } from 'services/EpdClient';
import styled from 'styled-components';
import { FieldContainer, FieldPanelInput } from 'styles/v2/Styles.styled';
import { EPDProductType, Option } from 'types/types';

type SectionProps = {
  epd?: EPDModel;
  biogenicCarbonContentUnits?: Option[];
  onChangeEpd: any;
};

const BiogenicCarbonContentCopySection: React.FC<SectionProps> = ({ epd, biogenicCarbonContentUnits, onChangeEpd }) => {
  const { t } = useTranslation();
  const isRequiredProductType =
    epd?.productType === EPDProductType.Goods &&
    epd?.pcr?.registrationNumber?.startsWith('2019:14') &&
    epd?.pcr?.is15804Compliant;
  const isProductContentRequired = epd?.biogenicCarbonContent?.doesProductContainMoreThan5Percent === YES_NO.YES;
  const isPackageContentRequired = epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5Percent === YES_NO.YES;

  function handleConversionFactorChange(event: any) {
    const { value } = event.target;
    const formattedProductFlowValue = value.replace(/^0+(?=\d)/, ''); // Removes leading zeros, except one before a period
    event.target.value = formattedProductFlowValue;
  }

  const handleConversionFactorKeyDown = (event: any) => {
    const {
      code,
      target: { value },
    } = event;
    const fractionalNumWithOneDigitPattern = /^0\.\d$/;
    if (code === 'Backspace' && fractionalNumWithOneDigitPattern.test(value)) {
      event.target.value = '';
      return;
    }
    if (code !== 'Minus') {
      return;
    }
    event.preventDefault();
  };

  const handleConversionFactorPaste = (event: any) => {
    event.preventDefault();
    const clipboardData = event.clipboardData || window.Clipboard;
    const pastedValue = clipboardData.getData('text');

    if (pastedValue === '0') {
      // Inserts the text at the current caret position
      document.execCommand('insertText', false, '0.01');
      return;
    }

    let formattedProductFlowValue = pastedValue
      .replace(/^0+(?=\d)/, '') // Removes leading zeros, except one before a period
      .replace(/-/g, ''); // Removes minuses

    document.execCommand('insertText', false, formattedProductFlowValue);
  };

  const handleChangeProperty = (propertyName: string, value: any) => {
    const updatedBiogenicCarbonContent = changeBiogenicCarbonContentValues(propertyName, value);
    onChangeEpd('biogenicCarbonContent', updatedBiogenicCarbonContent);
  };

  const changeBiogenicCarbonContentValues = (name: string, value?: string) => {
    switch (name) {
      case 'doesProductContainMoreThan5Percent':
        return {
          ...epd?.biogenicCarbonContent,
          doesProductContainMoreThan5Percent: value,
        };
      case 'doesPackagingMaterialContainMoreThan5Percent':
        return {
          ...epd?.biogenicCarbonContent,
          doesPackagingMaterialContainMoreThan5Percent: value,
        };
      case 'productItemUnitKey':
        return {
          ...epd?.biogenicCarbonContent,
          productItem: { ...epd?.biogenicCarbonContent?.productItem, unitKey: value },
        };
      case 'productItemConversionFactor':
        return {
          ...epd?.biogenicCarbonContent,
          productItem: { ...epd?.biogenicCarbonContent?.productItem, conversionFactor: value },
        };
      case 'accompanyingPackageItemUnitKey':
        return {
          ...epd?.biogenicCarbonContent,
          accompanyingPackageItem: { ...epd?.biogenicCarbonContent?.accompanyingPackageItem, unitKey: value },
        };
      case 'accompanyingPackageItemConversionFactor':
        return {
          ...epd?.biogenicCarbonContent,
          accompanyingPackageItem: { ...epd?.biogenicCarbonContent?.accompanyingPackageItem, conversionFactor: value },
        };
      default:
        return { ...epd?.biogenicCarbonContent, [name]: value };
    }
  };

  return (
    <>
      <FieldContainer>
        <FieldPanelInput>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.title')}</span>
            <TooltipHelpIcon content={t('epdWizard.wizardStep2.properties.biogenicCarbon.tooltip')} position={'right'} />
          </LabelGroup>
          <HelpBox>{t('epdWizard.wizardStep2.properties.biogenicCarbon.infoBox')}</HelpBox>
        </FieldPanelInput>
        <FieldContainer>
          <WizardSelectComponent
            required={isRequiredProductType}
            label={t('epdWizard.wizardStep2.properties.biogenicCarbon.product5percent')}
            options={YesNoDataSource}
            value={YesNoDataSource.find(
              (item) => item.value === epd?.biogenicCarbonContent?.doesProductContainMoreThan5Percent
            )}
            name="doesProductContainMoreThan5Percent"
            onChanged={handleChangeProperty}
          />
        </FieldContainer>
        <FieldContainer>
          <WizardSelectComponent
            required={isRequiredProductType}
            label={t('epdWizard.wizardStep2.properties.biogenicCarbon.package5percent')}
            options={YesNoDataSource}
            value={YesNoDataSource.find(
              (item) => item.value === epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5Percent
            )}
            name="doesPackagingMaterialContainMoreThan5Percent"
            onChanged={handleChangeProperty}
          />
        </FieldContainer>
        <FieldContainer>
          <FieldContainer>
            <SmartLabel label={t('epdWizard.wizardStep2.properties.biogenicCarbon.product')} />
          </FieldContainer>
          <FieldPanel>
            <WizardSelectComponent
              required={isProductContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.propertyUnit')}
              options={biogenicCarbonContentUnits ?? []}
              value={biogenicCarbonContentUnits?.find(
                (item) => item.value === epd?.biogenicCarbonContent?.productItem?.unitKey?.toString()
              )}
              name="productItemUnitKey"
              onChanged={handleChangeProperty}
            />
            <WizardTextInputComponent
              required={isProductContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.value')}
              value={epd?.biogenicCarbonContent?.productItem?.conversionFactor}
              type="number"
              min="0.1"
              step="0.1"
              name="productItemConversionFactor"
              tooltip="Numeric field."
              onKeyDown={handleConversionFactorKeyDown}
              onPaste={handleConversionFactorPaste}
              onChange={handleConversionFactorChange}
              onChanged={handleChangeProperty}
            />
          </FieldPanel>
        </FieldContainer>

        <FieldContainer>
          <FieldContainer>
            <SmartLabel label={t('epdWizard.wizardStep2.properties.biogenicCarbon.package')} />
          </FieldContainer>
          <FieldPanel>
            <WizardSelectComponent
              required={isPackageContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.propertyUnit')}
              options={biogenicCarbonContentUnits ?? []}
              value={biogenicCarbonContentUnits?.find(
                (item) => item.value === epd?.biogenicCarbonContent?.accompanyingPackageItem?.unitKey?.toString()
              )}
              name="accompanyingPackageItemUnitKey"
              onChanged={handleChangeProperty}
            />
            <WizardTextInputComponent
              required={isPackageContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.value')}
              value={epd?.biogenicCarbonContent?.accompanyingPackageItem?.conversionFactor}
              type="number"
              min="0.1"
              step="0.1"
              name="accompanyingPackageItemConversionFactor"
              tooltip="Numeric field."
              onKeyDown={handleConversionFactorKeyDown}
              onPaste={handleConversionFactorPaste}
              onChange={handleConversionFactorChange}
              onChanged={handleChangeProperty}
            />
          </FieldPanel>
        </FieldContainer>
      </FieldContainer>
    </>
  );
};

const FieldPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;
  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

export default BiogenicCarbonContentCopySection;
